import { Form, useActionData } from 'react-router'
import { SyrchaLogo } from '#app/components/ui/syrcha-logo.tsx'
import { type action } from '#app/root.tsx'

export function ComingSoon() {
	const actionData = useActionData<typeof action>()
	const isSuccess = actionData?.status === 'success'

	// `marketing-surface dark` is what every other marketing surface wraps itself
	// in, and this one is rendered bare from root.tsx. Without it the
	// theme-scoped status tokens resolve to their light values on a permanently
	// dark ground - `--marketing-danger` came out at 4.00:1, and is darker still
	// since #612 - and the surface's 2px cyan `:focus-visible` outline never
	// applied to the form.
	return (
		<div className="marketing-surface dark relative flex min-h-[100dvh] flex-col items-center justify-center gap-16 overflow-hidden bg-[var(--marketing-base)] px-6 py-12 text-[var(--marketing-black)]">
			{/* Decorative rotating square. `aria-hidden` because it is a mark, not
			    content, and `overflow-hidden` on the parent because at 256px it
			    hangs off the right edge below about 600px and gave the page a
			    horizontal scrollbar. Hidden outright on phones, where it sits
			    behind the headline rather than beside it. */}
			<div
				aria-hidden
				className="pointer-events-none absolute top-[15%] right-[10%] hidden opacity-[0.04] sm:block"
			>
				<div className="h-64 w-64 rotate-[15deg] border border-[var(--marketing-black)]" />
			</div>

			<div className="relative z-10 flex max-w-md flex-col items-center text-center">
				<SyrchaLogo className="h-16 w-16" />
				<h1 className="mt-8 font-[family-name:var(--font-display)] text-[44px] leading-[1.02] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[56px]">
					Privacy Compliance, Automated.
				</h1>
				<p className="mt-4 text-lg leading-relaxed text-[var(--marketing-gray)]">
					We're building something great. Be the first to know when we launch.
				</p>

				{isSuccess ? (
					<div className="mt-8 rounded-md border border-[var(--marketing-accent)] bg-[var(--marketing-accent)]/10 px-6 py-4">
						<p className="font-[family-name:var(--font-display)] font-semibold text-[var(--marketing-accent)]">
							You're on the list!
						</p>
					</div>
				) : (
					// Stacked below sm: an email field and "Notify Me" side by side
					// leaves the field under 10 characters wide on a 320px screen.
					<Form
						method="post"
						className="mt-8 flex w-full flex-col gap-3 sm:flex-row"
					>
						<label htmlFor="coming-soon-email" className="sr-only">
							Email address
						</label>
						<input
							id="coming-soon-email"
							type="email"
							name="email"
							required
							autoComplete="email"
							aria-describedby={
								actionData?.status === 'error'
									? 'coming-soon-email-error'
									: undefined
							}
							aria-invalid={actionData?.status === 'error' || undefined}
							placeholder="you@example.com"
							className="auth-input flex-1"
						/>
						<button
							type="submit"
							className="btn-marketing w-full shrink-0 sm:w-auto"
						>
							<span>Notify Me</span>
						</button>
					</Form>
				)}

				{actionData?.status === 'error' && (
					// The message arrives on a POST response while focus is still in
					// the field, so nothing else would announce it.
					<p
						id="coming-soon-email-error"
						role="alert"
						className="mt-3 text-sm text-[var(--marketing-danger)]"
					>
						{actionData.error}
					</p>
				)}
			</div>

			{/* In flow, not pinned. As `absolute bottom-8` inside a centred
			    `min-h-screen` column it sat on top of the form once the viewport
			    got short - a landscape phone, or any window under ~640px tall. */}
			<footer className="relative z-10 text-sm text-[var(--marketing-gray)]">
				© {new Date().getFullYear()} Syrcha. All rights reserved.
			</footer>
		</div>
	)
}
